import React, { useState, useEffect } from 'react';
import Container from 'react-bootstrap/Container';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Accordion from 'react-bootstrap/Accordion';
import Button from 'react-bootstrap/Button';
import Label from 'react-bootstrap/FormLabel';
import { showToast as toast } from '../toastManager';
import axios from 'axios';
import { verifySdkPopup, describeVerifyFailure } from '../utils/verifySdkPopup';

const Setup = ({ ViewedPage, shop_domain, app_url }) => {
  useEffect(() => { ViewedPage && ViewedPage(32); }, [ViewedPage]);
  // Dynamic SDK languages and templates
  const [sdkLanguages, setSdkLanguages] = useState([]);
  const [sdkTemplates, setSdkTemplates] = useState({});
  const [sdkLanguage, setSdkLanguage] = useState('');
  const [script, setScript] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [testStatus, setTestStatus] = useState('');
  const [scriptTag, setScriptTag] = useState('');
  const [activeKeys, setActiveKeys] = useState(['0', '1']);

  useEffect(() => {
    // Fetch available SDK languages and templates on mount
    const fetchSdkTemplates = async () => {
      try {
        const response = await axios.get(app_url + '/api/store/sdk-script-templates', { withCredentials: true });
        const data = response.data;
        if (data.success && data.languages && data.templates) {
          setSdkLanguages(data.languages.map(lang => ({ value: lang, label: lang.charAt(0).toUpperCase() + lang.slice(1) })));
          setSdkTemplates(data.templates);
          setSdkLanguage(data.languages[0] || '');
        } else {
          toast.error('Failed to fetch SDK languages.');
        }
      } catch (err) {
        toast.error('Network or server error while fetching SDK languages.');
      }
    };
    fetchSdkTemplates();
  }, []);

  const handleGetCode = async () => {
    setError('');
    setScript('');
    setLoading(true);
    try {
      const response = await axios.post(app_url + '/api/cux/connection-sdk-script', 
        { language: sdkLanguage, shop_domain }, 
        { withCredentials: true }
      );
      const data = response.data;
      console.log('=== DEBUG: Get Code Response ===');
      console.log('response data:', data);
      
      if (data.success) {
        console.log('Script generated successfully');
        console.log('Generated script:', data.script);
        setScript(data.script);
        setScriptTag(data.script);
      } else {
        console.log('Script generation failed:', data.message);
        toast.error(data.message || 'Failed to generate script.');
      }
    } catch (err) {
      toast.error('Network or server error.');
    } finally {
      setLoading(false);
    }
  };

  const handleTestScript = async () => {
    setTestStatus('');
    if (!websiteUrl || !scriptTag) {
      if (!websiteUrl) {
        toast.error('Please enter a website URL.');
        return;
      }
      toast.error('Please generate the script first.');
      return;
    }
    // Extract clientKey from the scriptTag
    console.log('=== DEBUG: Client Key Extraction ===');
    console.log('scriptTag:', scriptTag);
    console.log('scriptTag length:', scriptTag.length);
    console.log('scriptTag type:', typeof scriptTag);
    
    // Try multiple regex patterns to handle different quote styles
    let clientKeyMatch = scriptTag.match(/data-cognitive-ux-site="([^"]+)"/); // Double quotes
    if (!clientKeyMatch) {
      clientKeyMatch = scriptTag.match(/data-cognitive-ux-site='([^']+)'/); // Single quotes
    }
    if (!clientKeyMatch) {
      clientKeyMatch = scriptTag.match(/data-cognitive-ux-site=([^"'\s>]+)/); // No quotes
    }
    
    console.log('clientKeyMatch:', clientKeyMatch);
    
    const clientKey = clientKeyMatch ? clientKeyMatch[1] : null;
    console.log('extracted clientKey:', clientKey);
    
    if (!clientKey) {
      console.log('ERROR: Could not extract client key from script');
      console.log('scriptTag content:', scriptTag);
      toast.error('Could not extract client key from script.');
      return;
    }
    try {
      const response = await axios.post(app_url + '/api/store/check-sdk-script',
        { url: websiteUrl, clientKey },
        { withCredentials: true }
      );
      const data = response.data;
      if (data.mode !== 'popup' || !data.popupUrl || !data.pollUrl) {
        toast.error(data.message || 'Could not start verification. Please try again.');
        setTestStatus(null);
        return;
      }

      toast.info && toast.info('Opening your storefront in a new tab to verify the SDK…');

      const result = await verifySdkPopup({
        popupUrl: data.popupUrl,
        pollUrl: data.pollUrl,
        token: data.token,
        expiresInMs: data.expiresInMs
      });

      if (result.ok) {
        if (result.inHead) {
          setTestStatus({ type: 'success', message: 'SDK script is present in head section' });
        } else {
          toast.error('SDK is loaded but not inside the <head> tag. Please move it to <head>.');
          setTestStatus(null);
        }
      } else {
        toast.error(describeVerifyFailure(result));
        setTestStatus(null);
      }
    } catch (error) {
      toast.error('Could not start verification. Please check the URL and try again.');
      setTestStatus(null);
    }
  };

  return (
    <div className="main-container chatbot" style={{ position: 'relative' }}>
      <div className="main-title mt-4">
        <h2>SDK Setup</h2>
      </div>
      <div className="main-accordion-body mt-4 p-2">
        <Accordion activeKey={activeKeys} alwaysOpen onSelect={(k) => {
          if (!k) return;
          const keys = Array.isArray(k) ? k : [k];
          setActiveKeys(keys);
        }}>
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="sxi-accordion-header-with-icon">
                  {/* <div className="sxi-accordion-header-icon" aria-hidden="true">
                    <svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g filter="url(#filter_sdk)">
                        <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" shapeRendering="crispEdges"/>
                        <rect x="3" y="3" width="48" height="48" rx="6" stroke="#CEDEFF" shapeRendering="crispEdges"/>
                        <path d="M20 22L14 27L20 32M34 22L40 27L34 32M28 18L26 36" stroke="#2056C1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </g>
                      <defs>
                        <filter id="filter_sdk" x="0.5" y="0.5" width="57" height="57" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                          <feOffset dx="2" dy="2"/>
                          <feGaussianBlur stdDeviation="2"/>
                          <feComposite in2="hardAlpha" operator="out"/>
                          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
                          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                        </filter>
                      </defs>
                    </svg>
                  </div> */}
                  <span>SDK Language &amp; SDK Script</span>
                </div>
                <span className={`campaign-accordion-chevron ${activeKeys.includes('0') ? 'open' : ''}`} aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <Container fluid className="px-0">
                <Row className="mb-4 d-flex align-items-end flex-nowrap" style={{gap: 16}}>
                  <Col xs={12} md={8} className="mb-3 mb-md-0">
                    <Label htmlFor="sdkLanguage" className="charts_filter_label mt-3">Please paste this code at the beginning of the &lt;head&gt; section</Label>
                    <select
                      id="sdkLanguage"
                      value={sdkLanguage}
                      onChange={e => setSdkLanguage(e.target.value)}
                      className="form-control"
                      style={{ height: 35, fontSize: 16 }}
                    >
                      {sdkLanguages.map(lang => (
                        <option key={lang.value} value={lang.value}>{lang.label}</option>
                      ))}
                    </select>
                  </Col>
                  <Col xs={12} md={4} className="d-flex align-items-end" style={{paddingLeft: 0}}>
                    <Button
                      onClick={handleGetCode}
                      className="d-flex align-items-center justify-content-center sxi_primary_blue_btn btn-height"
                      style={{ minWidth: 220 }}
                      disabled={loading || !sdkLanguage}
                    >
                      {loading ? 'Generating...' : 'Get Code'}
                    </Button>
                  </Col>
                </Row>
                {script && (
                  <div style={{ marginTop: 24, background: '#fff', border: '1px solid #eee', borderRadius: 8, padding: 20, boxShadow: '0 2px 8px #f0f0f0' }}>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                      <div style={{ fontWeight: 'bold', fontSize: 16 }}>Your SDK Script:</div>
                      <button className="back-button btn-height" onClick={() => {
                        navigator.clipboard.writeText(script);
                        toast.success('SDK Script copied to clipboard');
                      }}>Copy</button>
                    </div>
                    <pre style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all', background: 'none', padding: 0, fontSize: 15 }}>{script}</pre>
                  </div>
                )}
              </Container>
            </Accordion.Body>
          </Accordion.Item>
          <Accordion.Item eventKey="1">
            <Accordion.Header>
              <div className="d-flex align-items-center justify-content-between w-100">
                <div className="sxi-accordion-header-with-icon">
                  {/* <div className="sxi-accordion-header-icon" aria-hidden="true">
                    <svg width="58" height="58" viewBox="0 0 58 58" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <g filter="url(#filter_test)">
                        <rect x="3" y="3" width="48" height="48" rx="6" fill="#E5EEFF" shapeRendering="crispEdges"/>
                        <rect x="3" y="3" width="48" height="48" rx="6" stroke="#CEDEFF" shapeRendering="crispEdges"/>
                        <path d="M27 15C20.373 15 15 20.373 15 27C15 33.627 20.373 39 27 39C33.627 39 39 33.627 39 27C39 20.373 33.627 15 27 15Z" stroke="#2056C1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M15 27H39M27 15C24.239 15 22 20.373 22 27C22 33.627 24.239 39 27 39C29.761 39 32 33.627 32 27C32 20.373 29.761 15 27 15Z" stroke="#2056C1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </g>
                      <defs>
                        <filter id="filter_test" x="0.5" y="0.5" width="57" height="57" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
                          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
                          <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
                          <feOffset dx="2" dy="2"/>
                          <feGaussianBlur stdDeviation="2"/>
                          <feComposite in2="hardAlpha" operator="out"/>
                          <feColorMatrix type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0.08 0"/>
                          <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow"/>
                          <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow" result="shape"/>
                        </filter>
                      </defs>
                    </svg>
                  </div> */}
                  <span>Test the script placement</span>
                </div>
                <span className={`campaign-accordion-chevron ${activeKeys.includes('1') ? 'open' : ''}`} aria-hidden="true">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M4.5 6L8 9.5L11.5 6" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </span>
              </div>
            </Accordion.Header>
            <Accordion.Body>
              <Container fluid className="px-0">
                <Row className="mb-4 d-flex align-items-end flex-nowrap" style={{gap: 16}}>
                  <Col xs={12} md={8} className="mb-3 mb-md-0">
                    <Label htmlFor="websiteUrl" className="charts_filter_label mt-2">Please enter the url to verify if the script is placed correctly</Label>
                    <input
                      type="text"
                      id="websiteUrl"
                      value={websiteUrl}
                      onChange={e => setWebsiteUrl(e.target.value)}
                      placeholder="Enter your website URL (e.g. https://example.com)"
                      className="form-control"
                      style={{ height: 35, fontSize: 16 }}
                    />
                  </Col>
                  <Col xs={12} md={4} className="d-flex align-items-end" style={{paddingLeft: 0}}>
                    <Button
                      onClick={handleTestScript}
                      className="d-flex align-items-center justify-content-center sxi_primary_blue_btn btn-height"
                      style={{ minWidth: 220 }}
                    >
                      Test
                    </Button>
                  </Col>
                </Row>
                {testStatus && testStatus.type === 'success' && (
                  <div
                    style={{
                      marginTop: 8,
                      fontWeight: 500,
                      color: 'green',
                      background: '#f6ffed',
                      border: '1px solid #b7eb8f',
                      borderRadius: 4,
                      padding: '8px 12px',
                      display: 'inline-block',
                    }}
                  >
                    {testStatus.message}
                  </div>
                )}
              </Container>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </div>
    </div>
  );
};

export default Setup; 